// src/components/AppShell.js

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  TextField,
  InputAdornment,
  Fab,
  useTheme,
  useMediaQuery,
  Divider,
  Collapse,
  Snackbar,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  Today as TodayIcon,
  Inbox as InboxIcon,
  Psychology as ReflectionsIcon,
  VolumeUp as VoicesIcon,
  Bookmark as BookmarkIcon,
  Tab as SessionIcon,
  AccountTree as ProjectIcon,
  Upload as ImportIcon,
  Settings as SettingsIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  Insights as InsightsIcon,
  Delete as TrashIcon,
  Timeline as ActivityIcon,
  HelpOutline as HelpIcon,
  Person as PersonIcon,
  Explore as ExploreIcon,
  Chat as ChatIcon,
  AutoAwesome as AskIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useExtensionSubscription } from '../hooks/useExtensionSubscription';
import WelcomeFlow from './WelcomeFlow';
import { useThemeMode } from '../contexts/ThemeContext';
import { useFeatureFlags } from '../contexts/FeatureFlagProvider';
import { useUIMetrics } from '../hooks/useUIMetrics';
import { useNavigate, useLocation } from 'react-router-dom';
import ResumeRecordingBanner from './ResumeRecordingBanner';
import QuickAddBookmarkDialog from './QuickAddBookmarkDialog';
import QuickAddNoteDialog from './QuickAddNoteDialog';
import NewProjectModal from './projects/NewProjectModal';
import LiveTranscriptionWindow from './LiveTranscriptionWindow';
import CommandBar from './CommandBar';
import { DRAWER_WIDTH } from '../constants/layout';

// Renders a single nav row used by both TOP_NAV and MORE_NAV. Pulled out so
// the two sections render the same way without code duplication. Quick-add
// IconButton (the inline + on Bookmarks / Notes) preserved here so it moves
// with the items into the More section in v3.
/**
 * Small persistent "Upgrade to Pro" pill in the app header.
 * Hides itself when the user is already on a pro tier so paid users don't
 * see nag CTAs. Uses useExtensionSubscription (extension tier is the one
 * that gates most features today — webapp tier is unified with it).
 */
function usePlanStatus() {
  // Wraps useExtensionSubscription so callers can render without the
  // provider being mounted on every route.
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const sub = useExtensionSubscription();
    return {
      isProUser: typeof sub.isPro === 'function' ? sub.isPro() : !!sub.isPro,
      loading: !!sub.loading,
      available: true,
    };
  } catch {
    return { isProUser: false, loading: true, available: false };
  }
}

function PlanMenuItem({ onUpgrade }) {
  const { isProUser, loading, available } = usePlanStatus();
  if (!available || loading) return null;
  if (isProUser) {
    return (
      <MenuItem sx={{ pointerEvents: 'none' }}>
        <WorkspacePremiumIcon sx={{ mr: 1.5, fontSize: 20, color: 'primary.main' }} />
        <Typography variant="body2">Blackmount Pro</Typography>
      </MenuItem>
    );
  }
  return (
    <MenuItem onClick={onUpgrade}>
      <WorkspacePremiumIcon sx={{ mr: 1.5, fontSize: 20, color: 'primary.main' }} />
      <Typography variant="body2" fontWeight={600}>Upgrade to Pro</Typography>
    </MenuItem>
  );
}

function UpgradeToProPill({ onClick }) {
  const { isProUser, loading, available } = usePlanStatus();
  if (!available || loading || isProUser) return null;
  return (
    <Button
      variant="contained"
      size="small"
      onClick={onClick}
      sx={{
        mr: 1.5,
        px: 1.75,
        py: 0.6,
        borderRadius: 999,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.82rem',
        bgcolor: 'primary.main',
        boxShadow: 'none',
        '&:hover': { boxShadow: '0 2px 8px rgba(37,99,235,0.25)' },
      }}
    >
      Upgrade to Pro
    </Button>
  );
}

function renderNavItem(item, { isActivePath, handleNavigation, setShowQuickAddBookmark, setShowQuickAddNote }) {
  const IconComponent = item.icon;
  const isActive = isActivePath(item.path);
  return (
    <ListItem
      key={item.label}
      onClick={() => handleNavigation(item.path)}
      sx={{
        mb: 0.5,
        borderRadius: 2,
        cursor: 'pointer',
        bgcolor: isActive ? `${item.color}15` : 'transparent',
        border: isActive ? `1px solid ${item.color}40` : '1px solid transparent',
        '&:hover': { bgcolor: `${item.color}10` },
      }}
    >
      <ListItemIcon sx={{ color: item.color, minWidth: 40 }}>
        <IconComponent />
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        secondary={item.subtitle}
        primaryTypographyProps={{
          fontWeight: isActive ? 600 : 400,
          color: isActive ? item.color : 'text.primary',
        }}
        secondaryTypographyProps={{ fontSize: '0.75rem' }}
      />
      {(item.label === 'Bookmarks' || item.label === 'Notes') && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (item.label === 'Bookmarks') setShowQuickAddBookmark(true);
            else if (item.label === 'Notes') setShowQuickAddNote(true);
          }}
          sx={{ color: item.color, opacity: 0.7, '&:hover': { opacity: 1 } }}
          title={item.label === 'Bookmarks' ? 'Add bookmark' : 'Add note'}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}
    </ListItem>
  );
}

const AppShell = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, userProfile, updateUserProfile, logout } = useAuth();

  const { darkMode, toggleDarkMode } = useThemeMode();
  // useFeatureFlags retained for future v4 toggling; currently unused after sidebar v3.
  // eslint-disable-next-line no-unused-vars
  const { get: getFlag } = useFeatureFlags();
  const { trackNavigation } = useUIMetrics();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar v3 (per Sriya user feedback 2026-05-03 + marketing-agent final synthesis):
  //   Top-level visible: Ask, Sessions, AI Chats (3 items — passes the 10-second test)
  //   Collapsed under "More": everything else (8 items)
  //   Footer: Settings, Help (account avatar lives in the AppBar, unchanged)
  // Defined inside the component so the icon component references resolve.
  // PR 4 (iOS-app-separation): nav is filtered by user's moduleMode.
  // Default 'all' preserves existing behavior — users with no moduleMode
  // field (existing users) see every item, no regression.
  const { useModuleMode, MODULE_NAV_VISIBILITY } = require('../hooks/useModuleMode');
  const { mode: moduleMode, setOverride: setModuleModeOverride } = useModuleMode();
  const navFilter = MODULE_NAV_VISIBILITY[moduleMode] || MODULE_NAV_VISIBILITY.all;

  const TOP_NAV_FULL = React.useMemo(() => [
    { label: 'Ask',      icon: AskIcon,     path: '/ask',     color: '#2563eb' },
    { label: 'Sessions', icon: SessionIcon, path: '/sessions', color: '#8b5cf6' },
    { label: 'AI Chats', icon: ChatIcon,    path: '/chats',   color: '#10A37F' },
  ], []);

  const MORE_NAV_FULL = React.useMemo(() => [
    { label: 'Bookmarks',   icon: BookmarkIcon,  path: '/bookmarks', color: '#ef4444' },
    { label: 'Voices',      icon: VoicesIcon,    path: '/voices',    color: '#f59e0b' },
    { label: 'Notes',       icon: InboxIcon,     path: '/inbox',     color: '#7c3aed' },
    { label: 'Projects',    icon: ProjectIcon,   path: '/projects',  color: '#10b981' },
    { label: 'Imports',     icon: ImportIcon,    path: '/imports',   color: '#6b7280' },
    { label: 'Recycle Bin', icon: TrashIcon,     path: '/trash',     color: '#ef4444' },
    { label: 'Insights',    icon: InsightsIcon,  path: '/insights',  color: '#10b981', subtitle: 'Knowledge Map' },
    { label: 'Activity',    icon: ActivityIcon,  path: '/activity',  color: '#3b82f6' },
  ], []);

  // Apply moduleMode filter — for mode='all' this is a no-op pass-through.
  const TOP_NAV = React.useMemo(() => TOP_NAV_FULL.filter(i => navFilter(i.path)), [TOP_NAV_FULL, navFilter]);
  const MORE_NAV = React.useMemo(() => MORE_NAV_FULL.filter(i => navFilter(i.path)), [MORE_NAV_FULL, navFilter]);
  // Detect filtered state so we can offer a "Show all modules" toggle in the sidebar.
  const isFiltered = moduleMode !== 'all';

  const FOOTER_NAV = React.useMemo(() => [
    { label: 'Settings', icon: SettingsIcon, path: '/settings' },
    { label: 'Help',     icon: HelpIcon,     path: '/help' },
  ], []);

  const morePathSet = React.useMemo(
    () => new Set(MORE_NAV.map(i => i.path)),
    [MORE_NAV]
  );

  // moreOpen state — initial value reads localStorage then falls back to
  // "open if user landed on a More-section route." Versioned key
  // ('sidebar.v3.*') so future restructures can reset cleanly.
  const [moreOpen, setMoreOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebar.v3.more.open');
      if (stored !== null) return stored === '1';
      return morePathSet.has(location.pathname);
    } catch {
      return false;
    }
  });

  // Persist moreOpen toggle as user preference
  React.useEffect(() => {
    try {
      localStorage.setItem('sidebar.v3.more.open', moreOpen ? '1' : '0');
    } catch { /* localStorage unavailable */ }
  }, [moreOpen]);

  // Auto-open More when user navigates to a More-section route via URL or
  // deep link. Doesn't write to localStorage — this is a transient expand
  // for "show me what I asked for", not a preference change.
  React.useEffect(() => {
    if (!moreOpen && morePathSet.has(location.pathname)) {
      setMoreOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Track sidebar v3 view (was nav.order.v2 — kept for analytics continuity)
  React.useEffect(() => {
    trackNavigation('v3', 'view');
  }, [trackNavigation]);

  // One-time Snackbar explaining the simplified sidebar to existing users.
  // Show conditions (refined per codex review): not yet dismissed AND
  // currently on a top-3 route AND More is closed. We never auto-show on
  // a More route because the auto-open effect already surfaces what the
  // user came for.
  const [showSidebarNotice, setShowSidebarNotice] = useState(() => {
    try {
      return localStorage.getItem('sidebar.v3.notice.dismissed') !== '1';
    } catch {
      return false;
    }
  });
  const sidebarNoticeEligible =
    showSidebarNotice &&
    !moreOpen &&
    TOP_NAV.some(item => item.path === location.pathname);

  const dismissSidebarNotice = () => {
    setShowSidebarNotice(false);
    try { localStorage.setItem('sidebar.v3.notice.dismissed', '1'); } catch { /* noop */ }
  };

  const handleSidebarNoticeShowMe = () => {
    setMoreOpen(true);
    dismissSidebarNotice();
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [showQuickAddBookmark, setShowQuickAddBookmark] = useState(false);
  const [showQuickAddNote, setShowQuickAddNote] = useState(false);
  const [showQuickAddProject, setShowQuickAddProject] = useState(false);
  const [showCommandBar, setShowCommandBar] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Command Bar shortcut (Cmd/Ctrl+K)
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        // Ignore when focus is in input, textarea, contenteditable, select or modal is open
        const isInInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName);
        const isContentEditable = event.target.contentEditable === 'true';
        const hasModalOpen = document.querySelector('[role="dialog"][aria-modal="true"]');
        
        if (!isInInput && !isContentEditable && !hasModalOpen) {
          event.preventDefault();
          setShowCommandBar(true);
          return;
        }
      }
      
      // Other shortcuts - only trigger if no input is focused and no modal is open
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
      
      if (event.metaKey || event.ctrlKey) {
        if (event.shiftKey) {
          if (event.key === 'B') {
            event.preventDefault();
            setShowQuickAddBookmark(true);
          } else if (event.key === 'N') {
            event.preventDefault();
            setShowQuickAddNote(true);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
    handleUserMenuClose();
  };

  const handleNavigation = (path) => {
    // Track navigation usage
    // Search across all 3 nav buckets so analytics still works
    const navigationItem =
      TOP_NAV.find(i => i.path === path) ||
      MORE_NAV.find(i => i.path === path) ||
      FOOTER_NAV.find(i => i.path === path);
    if (navigationItem) {
      trackNavigation('v3', 'click', navigationItem.label);
    }
    
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };
  
  // Auto-redirect /today to /inbox for legacy URLs
  React.useEffect(() => {
    if (location.pathname === '/today') {
      navigate('/inbox', { replace: true });
    }
  }, [location.pathname, navigate]);

  // No sidebar for unauthenticated users (after all hooks to satisfy rules-of-hooks)
  if (!currentUser) return <>{children}</>;

  const isActivePath = (path) => location.pathname === path;

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}>
      {/* Logo/Brand Area */}
      <Box sx={{ 
        p: 3, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        textAlign: 'center',
      }}>
        <Box
          component="img"
          src="/blackmount-logo.jpg"
          alt="BlackMount.AI"
          sx={{
            maxWidth: '160px',
            height: 'auto',
            mb: 1,
            borderRadius: 1
          }}
        />
        <Typography variant="caption" color="text.secondary" display="block">
          Your AI growth companion
        </Typography>
      </Box>


      {/* Navigation — sidebar v3.
          Top: Ask, Sessions, AI Chats (always visible).
          More: 8 items collapsed by default; click toggle to expand.
          Footer: Settings + Help, anchored to bottom of drawer. */}
      <Box sx={{ flex: 1, px: 2, display: 'flex', flexDirection: 'column' }}>
        {/* TOP NAV — the 3 always-visible primary items */}
        <List>
          {TOP_NAV.map((item) => renderNavItem(item, {
            isActivePath, handleNavigation, setShowQuickAddBookmark, setShowQuickAddNote,
          }))}
        </List>

        <Divider sx={{ my: 1.5 }} />

        {/* MORE TOGGLE + COLLAPSIBLE LIST */}
        <List sx={{ pt: 0 }}>
          <ListItem
            onClick={() => setMoreOpen(o => !o)}
            sx={{
              borderRadius: 2,
              cursor: 'pointer',
              py: 0.5,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            aria-expanded={moreOpen}
            aria-label={moreOpen ? 'Collapse More' : 'Expand More'}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              {moreOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary="More"
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: '0.78rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'text.secondary',
              }}
            />
          </ListItem>
        </List>
        <Collapse in={moreOpen} timeout="auto" unmountOnExit>
          <List sx={{ pt: 0 }}>
            {MORE_NAV.map((item) => renderNavItem(item, {
              isActivePath, handleNavigation, setShowQuickAddBookmark, setShowQuickAddNote,
            }))}
          </List>
        </Collapse>

        {/* PR 4: Show-all toggle when nav is filtered by moduleMode.
            Lets users instantly access hidden modules without going to Settings.
            Cleared via localStorage override; backend preference stays unchanged. */}
        {isFiltered && (
          <Box sx={{ px: 1, mt: 1 }}>
            <ListItem
              onClick={() => setModuleModeOverride('all')}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                py: 0.5,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              aria-label="Show all modules"
              title={`Currently filtered to "${moduleMode}" mode. Click to show all modules.`}
            >
              <ListItemText
                primary="Show all modules"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                }}
              />
            </ListItem>
          </Box>
        )}

        {/* SPACER pushes footer to the bottom */}
        <Box sx={{ flex: 1 }} />

        {/* FOOTER — Settings + Help (account avatar lives in AppBar) */}
        <Divider sx={{ my: 1 }} />
        <List sx={{ pt: 0 }}>
          {FOOTER_NAV.map((item) => {
            const IconComponent = item.icon;
            const isActive = isActivePath(item.path);
            return (
              <ListItem
                key={item.label}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: isActive ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ color: 'text.secondary', minWidth: 36 }}>
                  <IconComponent fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    color: 'text.secondary',
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flex: 1 }} />

          {/* Upgrade to Pro pill — always visible for signed-in users on any tier
              except pro. Highest-visibility upgrade surface across the app. */}
          <UpgradeToProPill onClick={() => navigate('/upgrade')} />

          {/* Theme Toggle */}
          <IconButton
            color="inherit"
            onClick={toggleDarkMode}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            sx={{ mr: 1 }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* User Menu */}
          <IconButton
            onClick={handleUserMenuOpen}
            aria-label="Account menu"
            sx={{ p: 0.75 }}
          >
            <Avatar
              src={currentUser?.photoURL}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
              }}
            >
              {(userProfile?.displayName?.[0] || userProfile?.name?.[0] || currentUser?.displayName?.[0] || currentUser?.email?.[0] || 'U').toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            <MenuItem onClick={handleUserMenuClose} sx={{ pointerEvents: 'none' }}>
              <Box>
                <Typography variant="subtitle2">
                  {userProfile?.displayName || userProfile?.name || currentUser?.displayName || currentUser?.email}
                </Typography>
                {userProfile?.username && (
                  <Typography variant="caption" color="text.secondary">
                    @{userProfile.username}
                  </Typography>
                )}
              </Box>
            </MenuItem>
            <Divider />
            {userProfile?.username && (
              <MenuItem onClick={() => { handleUserMenuClose(); navigate(`/u/${userProfile.username}`); }}>
                <PersonIcon sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2">View Profile</Typography>
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleUserMenuClose(); navigate('/explore'); }}>
              <ExploreIcon sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">Explore</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings'); }}>
              <SettingsIcon sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">Settings</Typography>
            </MenuItem>
            <PlanMenuItem onUpgrade={() => { handleUserMenuClose(); navigate('/upgrade'); }} />
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              border: 'none',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px', // AppBar height
          minHeight: 'calc(100vh - 64px)',
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ p: 2, pb: 0 }}>
          <ResumeRecordingBanner />
        </Box>
        {children}
      </Box>

      {/* Mobile FAB for Quick Add */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
          onClick={() => navigate('/dashboard')}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Welcome walkthrough — fires globally (any authenticated route)
          for users who haven't completed onboarding yet. Backend truth
          (userProfile.onboardingCompletedAt) drives visibility; on
          complete the modal POSTs /api/auth/complete-onboarding which
          persists the state, so it never fires again for that user on
          any device. Previously this only fired on Notes-tab visits
          via a fragile localStorage flag — see NotesWorkspace.js
          comment for the migration notes. */}
      <WelcomeFlow
        open={!!currentUser && !!userProfile && !userProfile.onboardingCompletedAt}
        onClose={() => {
          // Directly mutating userProfile does not trigger a React render.
          // Update context state so the dialog's open condition immediately clears.
          updateUserProfile({ onboardingCompletedAt: new Date().toISOString() });
        }}
      />

      {/* Quick Add Dialogs */}
      <QuickAddBookmarkDialog
        open={showQuickAddBookmark}
        onClose={() => setShowQuickAddBookmark(false)}
        projectId={null}
        onBookmarkAdded={null}
      />
      <QuickAddNoteDialog
        open={showQuickAddNote}
        onClose={() => setShowQuickAddNote(false)}
      />
      <NewProjectModal
        open={showQuickAddProject}
        onClose={() => setShowQuickAddProject(false)}
        onCreated={(project) => {
          setShowQuickAddProject(false);
          navigate(`/projects/${project._id}`);
        }}
      />

      {/* Live Transcription Window - appears during recording */}
      <LiveTranscriptionWindow />

      {/* Command Bar - always render it, let it handle its own visibility */}
      <CommandBar
        open={showCommandBar}
        onClose={() => setShowCommandBar(false)}
      />

      {/* Sidebar v3 simplification notice — fires once for existing users
          who land on a top-level route (Ask/Sessions/AI Chats) so they
          notice the demoted items before going looking for them. Skipped
          when on a More route (auto-open already surfaces the destination)
          or when More is already open. Dismiss / "Show me" both persist
          forever in localStorage. */}
      <Snackbar
        open={sidebarNoticeEligible}
        autoHideDuration={12000}
        onClose={(_e, reason) => {
          if (reason === 'clickaway') return;
          dismissSidebarNotice();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message="Sidebar simplified. Bookmarks, Voice, Notes, and other tools are under More."
        action={
          <>
            <Button color="primary" size="small" onClick={handleSidebarNoticeShowMe} sx={{ textTransform: 'none' }}>
              Show me
            </Button>
            <Button color="inherit" size="small" onClick={dismissSidebarNotice} sx={{ textTransform: 'none' }}>
              Got it
            </Button>
          </>
        }
      />
    </Box>
  );
};

export default AppShell;
